import React from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Filter } from "lucide-react";
import { FilterConfig, FilterValue, FilterType } from "../types";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";

export interface FilterMenuProps {
  availableFilters: FilterConfig[];
  activeFilters: FilterValue[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onFilterSelect: (type: FilterType) => void;
}

/**
 * Filter menu dropdown component for adding new filters.
 * Shows available filter types and their status (already active, no options available).
 */
export function FilterMenu({
  availableFilters,
  activeFilters,
  isOpen,
  onOpenChange,
  onFilterSelect,
}: FilterMenuProps) {
  const { t } = usePlatformTranslation();

  // Check if a filter type is already active
  const isFilterActive = (type: FilterType) => {
    return activeFilters.some(f => f.type === type);
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`p-2 hover:text-foreground hover:bg-muted ${
            activeFilters.length > 0 ? 'text-purple-P1' : 'text-muted-foreground'
          }`}
          title={t('platform.cards.widgets.add_filter')}
        >
          <Filter className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="space-y-1">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            Add Filter
          </div>
          {availableFilters.map((filterConfig) => {
            const hasOptions = filterConfig.options && filterConfig.options.length > 0;
            const isAlreadyActive = isFilterActive(filterConfig.type);
            
            return (
              <Button
                key={filterConfig.type}
                variant="ghost"
                size="sm"
                className={`w-full justify-start text-sm ${
                  !hasOptions || isAlreadyActive 
                    ? 'text-muted-foreground cursor-not-allowed opacity-50' 
                    : 'hover:bg-muted'
                }`}
                onClick={() => hasOptions && !isAlreadyActive && onFilterSelect(filterConfig.type)}
                disabled={isAlreadyActive || !hasOptions}
                title={!hasOptions ? 'No options available' : isAlreadyActive ? 'Filter already active' : undefined}
              >
                {filterConfig.label}
                {!hasOptions && <span className="ml-auto text-xs text-muted-foreground">(empty)</span>}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default FilterMenu;

